import { initialState } from './state'
import {
  getPortfolioFolderTreeRoutine,
  getPortfolioFolderFilesRoutine,
  unassignPortfolioFileRoutine,
  bulkRemovePortfolioFilesRoutine,
  getPortfolioFolderUserCountsRoutine,
  uploadPortfolioDocumentsRoutine,
  loadPortfolioFilesByFolderRoutine,
  loadPortfolioFilesByCategoryRoutine,
  loadPortfolioFilesByBpRoutine,
  updatePortfolioLoadingProgressRoutine,
  resetPortfolioDocumentsRoutine,
} from './actions'

const AGGREGATE_QUERY_KEYS = ['buildingPassportId', 'search', 'fileTypeIds', 'uploaderIds', 'tagIds']

const recordAggregateQuery = (state, payload) => {
  if (!payload || payload.cursor) return state
  const lastAggregateQuery = AGGREGATE_QUERY_KEYS.reduce((acc, key) => (payload[key] != null ? { ...acc, [key]: payload[key] } : acc), {})
  return { ...state, lastAggregateQuery }
}

export default function portfolioDocumentsReducer(state = initialState, action) {
  switch (action.type) {
    case loadPortfolioFilesByFolderRoutine.TRIGGER:
    case loadPortfolioFilesByCategoryRoutine.TRIGGER:
      return recordAggregateQuery(state, action.payload)

    case getPortfolioFolderTreeRoutine.REQUEST:
      return { ...state, treeLoading: true }
    case getPortfolioFolderTreeRoutine.SUCCESS:
      return { ...state, tree: action.payload, treeLoading: false }
    case getPortfolioFolderTreeRoutine.FAILURE:
      return { ...state, treeLoading: false }

    case getPortfolioFolderFilesRoutine.SUCCESS: {
      const { folderId, page, data, total, query } = action.payload
      const existing = page > 1 ? (state.filesByFolder[folderId]?.items || []) : []
      const seen = new Set(existing.map(f => f.id))
      return {
        ...state,
        filesByFolder: {
          ...state.filesByFolder,
          [folderId]: { items: [...existing, ...(data || []).filter(f => !seen.has(f.id))], total, page, query: query || {} },
        },
      }
    }

    case unassignPortfolioFileRoutine.SUCCESS:
    case bulkRemovePortfolioFilesRoutine.SUCCESS: {
      const { folderId, fileIds = [] } = action.payload || {}
      const entry = folderId ? state.filesByFolder[folderId] : null
      if (!entry) return state
      const items = entry.items.filter(f => !fileIds.includes(f.id))
      const removed = entry.items.length - items.length
      return {
        ...state,
        filesByFolder: {
          ...state.filesByFolder,
          [folderId]: { ...entry, items, total: entry.total != null ? Math.max(0, entry.total - removed) : entry.total },
        },
      }
    }

    case getPortfolioFolderUserCountsRoutine.SUCCESS:
      return { ...state, userCounts: action.payload }

    case uploadPortfolioDocumentsRoutine.REQUEST:
      return { ...state, uploading: true }
    case uploadPortfolioDocumentsRoutine.SUCCESS:
    case uploadPortfolioDocumentsRoutine.FAILURE:
      return { ...state, uploading: false }

    case loadPortfolioFilesByFolderRoutine.REQUEST:
      return {
        ...state,
        loadingFolders: new Set([...state.loadingFolders, action.payload.folderId])
      }
    case loadPortfolioFilesByFolderRoutine.SUCCESS: {
      const { folderId, files, nextCursor, hasMore, replace } = action.payload
      const existing = replace ? [] : (state.portfolioFilesByFolder[folderId] || [])
      const seen = new Set(existing.map(f => f.id))
      const merged = [...existing, ...files.filter(f => !seen.has(f.id))]
      const newLoadingFolders = new Set(state.loadingFolders); newLoadingFolders.delete(folderId)
      return {
        ...state,
        portfolioFilesByFolder: { ...state.portfolioFilesByFolder, [folderId]: merged },
        cursorByKey: { ...state.cursorByKey, [folderId]: nextCursor },
        hasMoreByKey: { ...state.hasMoreByKey, [folderId]: hasMore },
        loadedFolders: new Set([...state.loadedFolders, folderId]),
        loadingFolders: newLoadingFolders,
      }
    }
    case loadPortfolioFilesByFolderRoutine.FAILURE: {
      const { folderId } = action.payload
      const newLoadingFolders = new Set(state.loadingFolders)
      newLoadingFolders.delete(folderId)
      const newLoadingProgress = { ...state.loadingProgress }
      delete newLoadingProgress[folderId]
      return {
        ...state,
        loadingFolders: newLoadingFolders,
        loadingProgress: newLoadingProgress
      }
    }

    case loadPortfolioFilesByCategoryRoutine.REQUEST:
      return {
        ...state,
        loadingFolders: new Set([...state.loadingFolders, action.payload.categoryId])
      }
    case loadPortfolioFilesByCategoryRoutine.SUCCESS: {
      const { categoryId, files, nextCursor, hasMore, replace } = action.payload
      const existing = replace ? [] : (state.portfolioFilesByFolder[categoryId] || [])
      const seen = new Set(existing.map(f => f.id))
      const merged = [...existing, ...files.filter(f => !seen.has(f.id))]
      const newLoadingFolders = new Set(state.loadingFolders); newLoadingFolders.delete(categoryId)
      return {
        ...state,
        portfolioFilesByFolder: { ...state.portfolioFilesByFolder, [categoryId]: merged },
        cursorByKey: { ...state.cursorByKey, [categoryId]: nextCursor },
        hasMoreByKey: { ...state.hasMoreByKey, [categoryId]: hasMore },
        loadedFolders: new Set([...state.loadedFolders, categoryId]),
        loadingFolders: newLoadingFolders,
      }
    }
    case loadPortfolioFilesByCategoryRoutine.FAILURE: {
      const { categoryId } = action.payload
      const newLoadingFolders = new Set(state.loadingFolders)
      newLoadingFolders.delete(categoryId)
      const newLoadingProgress = { ...state.loadingProgress }
      delete newLoadingProgress[categoryId]
      return {
        ...state,
        loadingFolders: newLoadingFolders,
        loadingProgress: newLoadingProgress
      }
    }

    case loadPortfolioFilesByBpRoutine.REQUEST: {
      const key = `bp:${action.payload.bpId}`
      return {
        ...state,
        loadingFolders: new Set([...state.loadingFolders, key])
      }
    }
    case loadPortfolioFilesByBpRoutine.SUCCESS: {
      const { bpId, files, partial } = action.payload
      const key = `bp:${bpId}`
      const newLoadingFolders = new Set(state.loadingFolders)
      const newLoadingProgress = { ...state.loadingProgress }
      let newLoadedFolders = state.loadedFolders

      if (!partial) {
        newLoadingFolders.delete(key)
        delete newLoadingProgress[key]
        newLoadedFolders = new Set([...state.loadedFolders, key])
      }

      return {
        ...state,
        portfolioFilesByBp: {
          ...state.portfolioFilesByBp,
          [bpId]: files
        },
        loadedFolders: newLoadedFolders,
        loadingFolders: newLoadingFolders,
        loadingProgress: newLoadingProgress
      }
    }
    case loadPortfolioFilesByBpRoutine.FAILURE: {
      const key = `bp:${action.payload.bpId}`
      const newLoadingFolders = new Set(state.loadingFolders)
      newLoadingFolders.delete(key)
      const newLoadingProgress = { ...state.loadingProgress }
      delete newLoadingProgress[key]
      return {
        ...state,
        loadingFolders: newLoadingFolders,
        loadingProgress: newLoadingProgress
      }
    }

    case updatePortfolioLoadingProgressRoutine.SUCCESS:
      return {
        ...state,
        loadingProgress: {
          ...state.loadingProgress,
          [action.payload.key]: { loaded: action.payload.loaded }
        }
      }

    case resetPortfolioDocumentsRoutine.TRIGGER:
      return {
        ...initialState,
        filesByFolder: {},
        userCounts: {},
        portfolioFilesByFolder: {},
        portfolioFilesByBp: {},
        loadedFolders: new Set(),
        loadingFolders: new Set(),
        loadingProgress: {},
        cursorByKey: {},
        hasMoreByKey: {}
      }

    default:
      return state
  }
}
